import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { IconCollection, IconSpinner, OutsideClickHandler, ResponsiveImage, UserBadgeList } from '../../../../components';
import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import {
  abbreviateName,
  createCommentBodyHelper,
  formatCreatedAt,
  formatDataForReplyComment,
  transformRawContent,
} from '../../../../util/circleHelper';
import { extractMentionedUsers } from '../../../../util/mentionHelper';
import { FormattedMessage } from '../../../../util/reactIntl';
import {
  createLikeDislikeComment,
  createReplyComment,
  deleteCommentFromPost,
  loadMoreReplyComments,
  toggelEditComment,
  toggelReplyDocumentReducer,
  toggelReplyGiphyReducer,
  toggelReplyImageReducer,
  toggelReplyVideoReducer,
  toggleReplyComment,
  toggleReplyEmojiReducer,
} from '../../CircleCommunityPage.duck';
import FeedDocumentAttachment from '../FeedDocumentAttachment/FeedDocumentAttachment';
import FeedInlineAttachments from '../FeedInlineAttachments';
import AddCommentForm from './AddCommentForm';
import css from './CircleFeedModal.module.css';
import CommentReplyContainer from './CommentReplyContainer';
import { CompositeDecorator, EditorState, convertToRaw, Modifier } from 'draft-js';
import { RenderContent } from '../CircleFeedCard/CircleFeedCard';
import { subscriptionPermissionRequest } from '../../../../ducks/user.duck';
import { checkElephantModalOpen, currentUserRole, getUserPermissions } from '../../../../util/data';
import { communityPostAccess, USER_TYPE_BASIC } from '../../../../util/types';
import InReviewCard from '../../../../components/InReviewCard/InReviewCard';

export const removeTags = body_text => {
  return body_text?.replace(/@\w+(\s\w+)?/g, '')?.trim();
};

const mentionStrategy = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  let matchArr, start;
  const regex = /@\w+/g; // Detect mentions starting with '@'
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};
const Mention = props => (
  <span style={{ backgroundColor: '#e8f5ff', color: '#0073e6' }}>{props.children}</span>
);

const compositeDecorator = new CompositeDecorator([
  {
    strategy: mentionStrategy,
    component: Mention,
  },
]);

const CommentContainer = props => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const {
    commentData,
    postId,
    spaceId,
    spaceName,
    spaceSlug,
    requestCreateReplyComment,
    onGiphyChange,
    onDocumentClick,
    onImageClick,
    onVideoClick,
    isGiphyOpen,
    isDocumentModal,
    isImageModal,
    isVideoUpload,
    isRequestSpace,
    onDeleteComment,
    currentCommunityMember,
    requestDeleteComment,
    onEditCommentClick,
    onEmojiClick,
    isEmojiOpen,
    is_comments_closed = false,
    requestDirectUpload,
    advancedSearchData,
    onAdvancedSearch,
    onToggleReply,
    isReplyComment,
    onLoadMoreReplyComments,
    loadCommentReplyInProgress,
    isFirstLoad,
    currentUser,
    onSetElephantVerificationModal = () => { },
    handleCloseFeedModal = () => { },
    usersBadgesMap = {},
    usersSubscriptionMap = {},
    usersStripeConnectionMap = {},
  } = props;

  const {
    body_text,
    is_liked,
    replies = [],
    author,
    created_at,
    user_likes_count,
    id: commentId,
    replies_count,
    tiptap_body,
    post_id: commentPostId,
    loadMoreReplies = {},
    flagged_for_approval_at = null,
  } = commentData || {};
  const { page = 1, per_page = 10, has_next_page = false, count = 0 } = loadMoreReplies || {};

  const userSubscriptionRole = currentUserRole(currentUser);
  const isBasicRole = userSubscriptionRole == USER_TYPE_BASIC;

  // <<<<<<<<<<<<< all access checked by this fn >>>>>>>>>>>>>>>>>>>>>
  // communityPostAccess >> user can create post or not
  const checkCreatePostAuth = getUserPermissions(userSubscriptionRole, communityPostAccess);

  useEffect(() => {
    // Run the effect only once on the first render
    if (isFirstLoad.current) {
      const params = new URLSearchParams(location.search);
      const commentId = params.get('commentId');

      if (commentId) {
        const element = document.getElementById(`comment-${commentId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      // Set to false after the first render
      isFirstLoad.current = false;
    }
  }, [location]);

  const { content = [] } = tiptap_body?.body || {};
  const { id: currentUserid, avatar_url: current_avatar_url, name: currentUserName } =
    currentCommunityMember || {};

  const { name, roles, headline, avatar_url, community_member_id } = author || {};

  const isCurrentUserComment = currentUserid === community_member_id;

  const [editorState, setEditorState] = useState(EditorState.createEmpty(compositeDecorator));

  const handleLikeDislike = (commentId, is_liked) => {
    if (!checkCreatePostAuth) {
      dispatch(subscriptionPermissionRequest(true));
      return;
    }
    const isNotPermission = checkElephantModalOpen(currentUser);
    if (isNotPermission) {
      handleCloseFeedModal();
      onSetElephantVerificationModal(true);
      return;
    }

    // Prepare notification parameters
    const notificationParams = {
      postId,
      spaceId,
      spaceName,
      spaceSlug,
      likerUserId: currentUser?.id?.uuid, // Use headline as the unique user identifier
      likerName: currentCommunityMember?.name,
    };

    dispatch(createLikeDislikeComment({ commentId, is_liked }, notificationParams));
  };
  const handleCommentCreation = async values => {
    const isNotPermission = checkElephantModalOpen(currentUser);
    if (isNotPermission) {
      handleCloseFeedModal();
      onSetElephantVerificationModal(true);
      return;
    }
    const contentState = editorState?.getCurrentContent();
    const rawContent = convertToRaw(contentState); // Convert to raw JS object
    const formateEditor = transformRawContent(rawContent);
    let params = createCommentBodyHelper(values, formateEditor);
    if (isReplyComment?.author?.community_member_id !== currentUserid) {
      params = formatDataForReplyComment(params, isReplyComment?.author);
    }

    // Extract mentioned users from the reply
    const mentionedUserSgids = extractMentionedUsers(rawContent);

    // Prepare notification parameters for reply notifications
    const notificationParams = {
      postId,
      spaceId,
      spaceName,
      spaceSlug,
      replierUserId: currentUser?.id?.uuid, // Use currentUser ID for consistency
      replierName: currentCommunityMember?.name,
      mentionedUserSgids, // Pass the mentioned users
    };

    const apiParams = {
      commentId,
      bodyParams: params,
      notificationParams, // Add notification parameters
    };
    
    try {
      // Wait for the reply to be created successfully
      const response = await dispatch(createReplyComment(apiParams));
      
      // Only clear the editor if the reply was created successfully
      if (response && !response.error) {
        setEditorState(EditorState.createEmpty(compositeDecorator));
        return response; // Return response to indicate success
      } else {
        // API returned an error response
        console.error('Failed to create reply:', response?.error || 'Unknown error');
        return null; // Return null to indicate failure
      }
    } catch (error) {
      // Don't clear the editor on error, so user can retry
      console.error('Failed to create reply:', error);
      return null; // Return null to indicate failure
    }
  };

  const handleDeleteComment = () => {
    // Pass spaceId and the comment author's headline (userId) for badge tracking
    onDeleteComment(postId, commentId, spaceId, currentUser?.id?.uuid);
    setIsOpen(false);
  };

  const handleEditComment = () => {
    onEditCommentClick(commentData);
    setIsOpen(false);
  };

  const insertEmoji = emoji => {
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    // Make sure the insertion is at the cursor's current position
    if (!selection.isCollapsed()) {
      const newContentState = Modifier.replaceText(currentContent, selection, emoji);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
    } else {
      // If the selection is collapsed (no text selected), append the emoji
      const newContentState = Modifier.insertText(currentContent, selection, emoji);
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      setEditorState(newEditorState);
    }
    onEmojiClick(false);
  };

  const handleLoadMoreReply = () => {
    const params = { page, per_page, commentId };
    onLoadMoreReplyComments(params);
  };

  const handleToggleReply = () => {
    if (!checkCreatePostAuth) {
      dispatch(subscriptionPermissionRequest(true));
      return;
    }
    const isNotPermission = checkElephantModalOpen(currentUser);
    if (isNotPermission) {
      handleCloseFeedModal();
      onSetElephantVerificationModal(true);
      return;
    }
    onToggleReply(commentData);
  };

  const hasAttachment = tiptap_body?.inline_attachments.length || tiptap_body?.attachments.length;

  return (
    <div className={css.scrollFeedSection} id={`comment-${commentId}`}>
      <div className={css.commentFeedsWrapper}>
        <div className={css.imageWrapper}>
          {avatar_url ? (
            <ResponsiveImage imagewithURL={avatar_url} />
          ) : (
            <div className={css.nameWrapper}>{abbreviateName(name)}</div>
          )}
          {headline && usersStripeConnectionMap[headline] ? (
            <div className={css.verfiedIcon}>
              <IconCollection name="profile_badge_stripe" />
            </div>
          ) : headline && usersSubscriptionMap[headline] ? (
            <div className={css.verfiedIcon}>
              <IconCollection name="profile_badge_elephant" />
            </div>
          ) : null}
        </div>

        <div data-testid="comment-container" className={css.nameDetails}>
          <div className={css.nameText}>
            <div className={css.nameTopRow}>
              <span className={css.authorName}>{name}</span>{' '}
              {flagged_for_approval_at ? (
                <span className={css.reviewText}>
                  <InReviewCard />{' '}
                </span>
              ) : null}{' '}
              {headline && usersBadgesMap[headline] && usersBadgesMap[headline].length > 0 && (
                <div className={css.userbagdeComment}>
                  <UserBadgeList badges={usersBadgesMap[headline]} size="small" />
                </div>
              )}
              <b>{formatCreatedAt(created_at)}</b>
            </div>

            {currentUser && isCurrentUserComment ? (
              <div className={css.dropdownDots}>
                <OutsideClickHandler onOutsideClick={() => isOpen && setIsOpen(false)}>
                  <span data-testid="comment-dropdown-dots" onClick={() => setIsOpen(!isOpen)} className={css.iconWrapper}>
                    <ProfileIconCard type="dot" />
                  </span>
                  {isOpen ? (
                    <div className={css.dropdownMenu}>
                      <ul>
                        <li onClick={handleEditComment}>
                          <FormattedMessage id="CircleFeedPage.editComment" />
                        </li>
                        <li onClick={handleDeleteComment}>
                          <FormattedMessage id="CircleFeedPage.deleteComment" />
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </OutsideClickHandler>
              </div>
            ) : null}
          </div>
          <RenderContent
            content={content}
            // onHandleItemClick={onHandleItemClick}
            // isShowMoreButtonVisible={true}
            sgids_to_object_map={tiptap_body?.sgids_to_object_map}
            dispatch={dispatch}
          />
          {hasAttachment ? (
            <>
              {!isRequestSpace ? (
                <div className={css.inlineAttachments}>
                  <FeedInlineAttachments
                    inline_attachments={tiptap_body?.inline_attachments}
                    attachments={tiptap_body?.attachments}
                    isRequestSpace={isRequestSpace}
                  />
                </div>
              ) : null}
              {/* show documents */}
              <FeedDocumentAttachment
                inline_attachments={tiptap_body?.inline_attachments}
                attachments={tiptap_body?.attachments}
              />
            </>
          ) : null}
          {/* {body_text ? <div className={css.detailsText}>{removeTags(body_text)}</div> : null} */}

          <div className={css.likedSection}>
            <div className={css.leftLikedSection}>
              <div className={css.likeText} onClick={() => handleLikeDislike(commentId, is_liked)}>
                {is_liked ? (
                  <span className={css.likedText}>Liked</span>
                ) : (
                  <span className={css.likeText}>Like</span>
                )}
              </div>
              {!is_comments_closed ? (
                <div className={css.likeText} onClick={handleToggleReply}>
                  Reply
                </div>
              ) : null}
            </div>
            <div className={css.likeCount}>
              {user_likes_count} {user_likes_count === 1 ? 'like' : 'likes'}
            </div>
            {/* load comments reply and send reply*/}
          </div>
          {replies?.length > 0 ? (
            <div className={css.CommentReplyContainer}>
              {replies?.length > 0 ? (
                <div>
                  {replies?.map(reply => (
                    <CommentReplyContainer
                      key={reply.id}
                      replies={reply}
                      handleLikeDislike={handleLikeDislike}
                      isRequestSpace={isRequestSpace}
                      onToggleReply={onToggleReply}
                      checkCreatePostAuth={checkCreatePostAuth}
                      postId={postId}
                      spaceId={spaceId}
                    />
                  ))}
                </div>
              ) : null}
              {loadCommentReplyInProgress ? (
                <div className={css.spinnerWrapper}>
                  <IconSpinner type={true} />
                </div>
              ) : replies.length < replies_count || has_next_page ? (
                <div className={css.showMoreButton} onClick={handleLoadMoreReply}>
                  <FormattedMessage id="CircleCommunity.loadMoreReplies" />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      {/* {requestCreateReplyComment ? (
        <div>
          <IconSpinner type={true} />
        </div>
      ) : null} */}
      {isReplyComment ? (
        <div className={css.commentReplySection}>
          {isReplyComment &&
          (isReplyComment?.id === commentId || isReplyComment?.parent_comment_id === commentId) ? (
            <AddCommentForm
              currentUser={currentUser}
              replyCommentPlaceHolder={`Replying to ${isReplyComment?.author?.name}`}
              editorState={editorState}
              setEditorState={setEditorState}
              insertEmoji={insertEmoji}
              onSubmit={handleCommentCreation}
              handleCloseFeedModal={handleCloseFeedModal}
              onSetElephantVerificationModal={onSetElephantVerificationModal}
              onGiphyChange={onGiphyChange}
              onDocumentClick={onDocumentClick}
              onImageClick={onImageClick}
              onVideoClick={onVideoClick}
              isGiphyOpen={isGiphyOpen}
              isDocumentModal={isDocumentModal}
              isImageModal={isImageModal}
              isVideoUpload={isVideoUpload}
              requestCreateComment={requestCreateReplyComment}
              requestDirectUpload={requestDirectUpload}
              onEmojiClick={onEmojiClick}
              isEmojiOpen={isEmojiOpen}
              avatar_url={avatar_url}
              name={name}
              advancedSearchData={advancedSearchData}
              onAdvancedSearch={onAdvancedSearch}
              currentUserName={currentUserName}
              current_avatar_url={current_avatar_url}
              isReplyComment={isReplyComment}
              currentUserHasActiveSubscription={currentUser?.id?.uuid && usersSubscriptionMap[currentUser.id.uuid] || isBasicRole}
              currentUserHasStripeConnected={currentUser?.id?.uuid && usersStripeConnectionMap[currentUser.id.uuid]}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  const {
    currentCommunityMember,
    requestDeleteComment,
    isReplyGiphyOpen: isGiphyOpen,
    isReplyDocumentModal: isDocumentModal,
    isReplyImageModal: isImageModal,
    isReplyVideoUpload: isVideoUpload,
    isReplyEmojiOpen: isEmojiOpen,
    isReplyComment,
    loadCommentReplyInProgress,
    usersBadgesMap,
    usersSubscriptionMap,
    usersStripeConnectionMap,
  } = state.CircleCommunityPage;

  return {
    isGiphyOpen,
    isDocumentModal,
    isImageModal,
    isVideoUpload,
    currentCommunityMember,
    requestDeleteComment,
    isEmojiOpen,
    isReplyComment,
    loadCommentReplyInProgress,
    currentUser,
    usersBadgesMap,
    usersSubscriptionMap,
    usersStripeConnectionMap,
  };
};
const mapDispatchToProps = dispatch => ({
  onGiphyChange: () => dispatch(toggelReplyGiphyReducer()),
  onDocumentClick: () => dispatch(toggelReplyDocumentReducer()),
  onImageClick: () => dispatch(toggelReplyImageReducer()),
  onVideoClick: () => dispatch(toggelReplyVideoReducer()),
  onDeleteComment: (postId, commentID, spaceId, commenterUserId) => dispatch(deleteCommentFromPost(postId, commentID, spaceId, commenterUserId)),
  onEditCommentClick: commentData => dispatch(toggelEditComment(commentData)),
  onEmojiClick: params => dispatch(toggleReplyEmojiReducer(params)),
  onToggleReply: data => dispatch(toggleReplyComment(data)),
  onLoadMoreReplyComments: params => dispatch(loadMoreReplyComments(params)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(CommentContainer);
